import React from 'react';
import { useSidebar } from '../../contexts/SidebarContext';

import {
  SquaresPlusIcon,
  TableCellsIcon,
  ArrowDownTrayIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const TABS = [
  {
    id: 'planner',
    label: 'Meal Planner',
    icon: SquaresPlusIcon,
    component: React.lazy(() => import('./MealPlanner')),
  },
  {
    id: 'grocery',
    label: 'Grocery List',
    icon: TableCellsIcon,
    component: React.lazy(() => import('./GroceryList')),
  },
  {
    id: 'export',
    label: 'Client Export',
    icon: ArrowDownTrayIcon,
    component: React.lazy(() => import('./ClientExportPanel')),
  },
  {
    id: 'reminder',
    label: 'Reminders',
    icon: ClockIcon,
    component: React.lazy(() => import('./ScheduleReminders')),
  },
];

export default function BottomDrawer() {
  const {
    bottomDrawerOpen,
    openBottomDrawer,
    closeBottomDrawer,
    activeBottomTab,
    setActiveBottomTab,
  } = useSidebar();

  // 🔥 ANDROID BACK BUTTON INTEGRATION — Bottom Drawer
  // Expose global close function for native back button
  window.closeBottomDrawer = () => {
    closeBottomDrawer();
  };

  const currentTab = TABS.find(t => t.id === activeBottomTab) || TABS[0];
  const ActiveComp = currentTab.component;

  return (
    <div
      className={`
      fixed bottom-0 inset-x-0 z-40 bg-white shadow-2xl min-w-0
      transition-transform duration-300 ease-out
      ${bottomDrawerOpen 
          ? 'translate-y-0' 
          : 'translate-y-[calc(100%-48px)] sm:translate-y-[calc(100%-56px)]'}
    `}
    >
      {/* Responsive Drawer Height */}
      <div className={`
      h-[min(200px,40vh)] sm:h-[min(240px,40vh)]
      lg:h-[min(280px,35vh)] xl:h-[min(320px,45vh)]
      flex flex-col min-w-0
    `}>
        <nav
          role="tablist"
          className="flex justify-between items-center border-b border-light-gray px-4 pt-3 pb-2 overflow-x-auto no-scrollbar whitespace-nowrap"
        >
          {TABS.map(tab => {
            const isActive = tab.id === activeBottomTab;
            const Icon = tab.icon;

            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                aria-label={tab.label}
                title={tab.label}
                onClick={() => {
                  setActiveBottomTab(tab.id);
                  openBottomDrawer();
                }}
                className={`
                relative flex items-center flex-shrink-0
                ${isActive ? 'px-4' : 'px-3'} py-3 rounded transition
                focus:outline-none focus-visible:ring-2 focus-visible:ring-rich-gold
                whitespace-nowrap
                ${isActive
                  ? 'bg-white text-deep-blue shadow-inner ring-2 ring-rich-gold'
                  : 'text-charcoal hover:bg-soft-yellow/40 hover:text-rich-gold'}
              `}
              >
                <Icon className="w-5 h-5" />
                {isActive && (
                  <span className="ml-2 max-w-[80px] truncate whitespace-nowrap text-sm font-medium">
                    {tab.label}
                  </span>
                )}
              </button>
            );
          })}

          <span className="flex-1" />

          <button
            onClick={closeBottomDrawer}
            aria-label="Close drawer"
            className="text-slate-gray hover:text-charcoal px-4 py-3"
          >
            ✕
          </button>
        </nav>

        {/* DRAWER CONTENT — SCROLLABLE + SAFE AREA */}
        <div
          role="tabpanel"
          aria-labelledby={`tab-${currentTab.id}`}
          className={`
          overflow-y-auto overscroll-contain scrollbar-none px-4 py-2
          pb-[env(safe-area-inset-bottom)]
          h-[calc(min(200px,40vh)-48px)]
          sm:h-[calc(min(240px,40vh)-56px)]
          lg:h-[calc(min(280px,35vh)-56px)]
          xl:h-[calc(min(320px,45vh)-64px)]
        `}
        >
          <React.Suspense fallback={<div className="text-center py-4">Loading…</div>}>
            <ActiveComp />
          </React.Suspense>
        </div>
      </div>
    </div>
  );
}